<template>
  <section class="min-h-screen bg-gray-100 pt-16 pb-8 px-4">
    <div class="max-w-7xl mx-auto space-y-10">
      <div class="text-center mb-14">
        <h2 class="text-4xl md:text-5xl font-extrabold tracking-tight text-primary-dark mb-4 drop-shadow-lg">
          Frequently Asked Questions
        </h2>
        <p class="text-base md:text-lg text-gray-700">
          Everything you need to know about TrendzTube
        </p>
      </div>
      <div class="space-y-4">
        <div 
          v-for="(faq, index) in faqs" 
          :key="index"
          class="bg-white rounded-lg shadow-sm border border-gray-200 transition overflow-hidden"
        >
          <button
            @click="toggleFaq(index)"
            class="w-full px-6 py-4 text-left bg-white hover:bg-primary/5 focus:outline-none focus:bg-primary/5 transition-colors duration-200 rounded-t-lg"
            :class="{ 'bg-primary/5': openFaqs.includes(index) }"
          >
            <div class="flex items-center justify-between">
              <h3 class="text-base md:text-lg font-semibold text-primary-dark">{{ faq.question }}</h3>
              <span 
                class="material-symbols-outlined text-primary transition-transform duration-200"
                :class="{ 'rotate-180': openFaqs.includes(index) }"
              >
                expand_more
              </span>
            </div>
          </button>
          <div 
            v-show="openFaqs.includes(index)"
            class="px-6 pb-4 text-sm text-gray-700 leading-relaxed bg-primary/5 rounded-b-lg"
          >
            {{ faq.answer }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'FAQ',
  data() {
    return {
      openFaqs: [],
      faqs: [
        {
          question: "What is TrendzTube and how does it work?",
          answer: "TrendzTube is an AI-powered platform that analyzes real YouTube trending data to generate personalized video ideas for your channel. For every idea, you'll also see which trending videos inspired it, complete with thumbnails and direct links to watch or study those videos."
        },
        {
          question: "Do I need to create an account to use TrendzTube?",
          answer: "Yes, you need to sign up for a free account to generate video ideas and access personalized features. This helps us track your usage and provide the best experience for your plan."
        },
        {
          question: "How many video ideas can I generate each month?",
          answer: "Each plan has a monthly generation limit. The Free Plan allows 3 generations per month (which is 18 video ideas in total), TrendzTube Starter allows 100, and TrendzTube Pro allows 500. You can upgrade your plan at any time for higher limits."
        },
        {
          question: "What happens if I reach my monthly generation limit?",
          answer: "Once you reach your plan's monthly generation limit, you'll need to wait for the next period or upgrade your plan to continue generating new video ideas."
        },
        {
          question: "Is the data really from YouTube?",
          answer: "Yes! TrendzTube uses YouTube's official API to fetch trending videos, view counts, engagement metrics, and more. All recommendations are based on real, up-to-date YouTube data."
        },
        {
          question: "How often is the trending data updated?",
          answer: "Trending data is updated several times a day, so you always get the latest trends and opportunities for your channel."
        },
        {
          question: "Which regions and categories are supported?",
          answer: "TrendzTube supports all major YouTube regions (like US, UK, Canada, Australia, and more) and all YouTube categories, including Gaming, Entertainment, Education, Technology, and others."
        },
        {
          question: "Can I use TrendzTube for any type of YouTube channel?",
          answer: "Absolutely! Whether you create gaming, educational, lifestyle, business, or any other type of content, TrendzTube analyzes trends specific to your chosen category and region."
        },
        {
          question: "How accurate are the video idea recommendations?",
          answer: "Our AI analyzes real performance data from trending videos, so recommendations are highly relevant and based on what’s working right now on YouTube. Each idea also shows the actual trending videos that inspired it, so you can see the source and learn from proven content."
        },
        {
          question: "Is there a free trial or free plan?",
          answer: "No, you need to sign up for an account to use TrendzTube. All plans require registration, and the Free Plan allows limited usage after sign up. You can upgrade to a paid plan for more features and higher limits."
        },
        {
          question: "Can I cancel anytime?",
          answer: "Yes! You can cancel your subscription at any time from your account settings. There are no long-term commitments or cancellation fees."
        },
        {
          question: "How can I quickly contact TrendzTube team?",
          answer: "You can reach us directly at tyteapps@gmail.com for any questions, support requests, or feedback. We aim to respond within 24 hours."
        },
        {
          question: "What do I get with each video idea?",
          answer: "Every video idea includes a title, description, tags, difficulty, trend strength, and a list of trending YouTube videos that inspired it. You can view thumbnails, watch the inspiration videos, or copy their links for further research."
        }
      ]
    };
  },
  methods: {
    toggleFaq(index) {
      if (this.openFaqs.includes(index)) {
        this.openFaqs = this.openFaqs.filter(i => i !== index);
      } else {
        this.openFaqs.push(index);
      }
    }
  }
};
</script>

<style scoped>
.shadow-sm {
  box-shadow: 0 2px 8px 0 rgba(80,36,255,0.06);
}
.rounded-lg {
  border-radius: 0.75rem;
}
.bg-white {
  --tw-bg-opacity: 1;
  background-color: rgb(255 255 255 / var(--tw-bg-opacity));
}
.bg-gray-100 {
  --tw-bg-opacity: 1;
  background-color: rgb(243 244 246 / var(--tw-bg-opacity));
}
.border-gray-200 {
  --tw-border-opacity: 1;
  border-color: rgb(229 231 235 / var(--tw-border-opacity));
}
</style>
